<script lang="ts" setup>
import type { AvailableDeliveryDateType } from '~/api/order';

const props = defineProps<{
  value: AvailableDeliveryDateType;
  dateOptions: AvailableDeliveryDateType[];
  dataTestId?: string;
}>();

const emit = defineEmits<{
  (event: 'update:value', value: AvailableDeliveryDateType): void;
}>();

const { locale } = useI18n();

const options = computed(() => {
  const weekDayOptions: Intl.DateTimeFormatOptions = { weekday: 'short' };
  const dateString: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
  };

  return props.dateOptions.map((el) => {
    const date = new Date(el.date);
    return {
      date: el.date,
      value: el,
      weekdayString: date
        .toLocaleDateString(locale.value, weekDayOptions)
        .toUpperCase(),
      dateString: date
        .toLocaleDateString(locale.value, dateString)
        .replace('.', ''),
    };
  });
});
</script>

<template>
  <div class="baseDayPicker" :data-test-id="dataTestId">
    <button
      v-for="item in options"
      :key="item.dateString"
      @click="emit('update:value', item.value)"
      class="baseDayPicker__item"
      :class="{ baseDayPicker__item_selected: item.date === value.date }"
      type="button"
      :data-test-id="dataTestId && `${dataTestId}-item`"
    >
      <span>{{ item.weekdayString }}</span>
      <span>{{ item.dateString }}</span>
    </button>
  </div>
</template>

<style lang="scss">
.baseDayPicker {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 10px;

  @include mq('sm') {
    grid-template-columns: repeat(4, 1fr);
  }

  &__item {
    height: 46px;
    border: 1px solid #e6e6e6;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    font-size: 12px;
    line-height: 120%;
    color: #121212;
    background-color: #fff;
    cursor: pointer;
    transition:
      background-color 0.3s ease,
      color 0.3s ease,
      border-color 0.3s ease;

    &:not(&_selected):hover {
      border-color: #121212;
    }

    &_selected {
      background-color: #121212;
      color: #fff;
      border-color: #121212;
    }
  }
}
</style>
